import { Box, Button, Grid2 as Grid, Stack, Typography } from "@mui/material"
import { Check } from "@phosphor-icons/react"
import React, { Fragment, useEffect, useState } from "react"
import { GenericAvatar } from "../../../components/generic/GenericAvatar"
import { GenericDivider } from "../../../components/generic/GenericDivider"
import { CollabRequestDialog } from "../../shared/CollabRequestDialog"
import { getNotifications, markAllNotificationAsRead, markNotificationAsRead } from "../../../config/apisauce"
import { INITIAL_GET_PARAMS, NEW_COLLAB_REQUEST_TITLE, NOTIFICATIONS_MESSAGE } from "../../../enums/generalEnums"
import { GenericIconLabel } from "../../../components/generic/GenericIconLabel"
import { Link, router } from "@inertiajs/react"
import { formatDistanceToNow } from 'date-fns';
import { CollabRequestUpdateDialog } from "../../shared/CollabRequestUpdateDialog"
import useStore from "../../../states/store"
import { GenericLinearLoader } from "../../../components/generic/GenericLoader"
import { ACCEPTED, CANCELLED } from "../../../enums/statusEnums"
import { PAGE_PERMISSIONS } from "../../../enums/permissions/permission"
import { BroadcastMessageDialog } from "../../shared/BroadcastMessageDialog"
import { BROADCAST_MESSAGE_TYPES } from "../../../enums/broadcastMessageEnums";
import { useNotificationActions } from './../../../store/notificationStore';
import { toSlug } from "../../../helpers/stringHelper";

export const NotificationList = ({ isTopBar = false, userId }) => {
  const [, { hasPermission }] = useStore();
  const [{ notificationsCount }, { decreaseNotificationCount, setNotificationsCount }] = useStore();
  const [openCollabRequestDialog, setOpenCollabRequestDialog] = useState(false);
  const [openCollabRequestUpdateDialog, setOpenCollabRequestUpdateDialog] = useState(false);
  const [openBroadcastMessageDialog, setOpenBroadcastMessageDialog] = useState(false);
  const [selectedBroadcastMessage, setSelectedBroadcastMessage] = useState({});
  const [selectedCollabRequest, setSelectedCollabRequest] = useState({});
  const [notifications, setNotifications] = useState({});
  const [filter, setFilter] = useState({ is_read: null });
  const [loading, setLoading] = useState(false);

  const fetchNotifications = async () => {
    setLoading(true);
    const response = await getNotifications({ ...INITIAL_GET_PARAMS, ...filter, user_id: userId });
    if (response.ok) {
      setNotifications(response?.data);
    }
    setLoading(false);
  };

  const handleMarkAllAsRead = async () => {
    const response = await markAllNotificationAsRead();
    if (response.ok) {
      setNotificationsCount(null);
      fetchNotifications();
    }
  };

  const handleCloseCollabRequestUpdateDialog = () => {
    setOpenCollabRequestUpdateDialog(false);
    sessionStorage.removeItem('collabHash');
    sessionStorage.removeItem('collabStatus');
  };

  const handleReadNotification = async (notificationId) => {
    const response = await markNotificationAsRead({ id: notificationId });
    if (response.ok) {
      if (filter?.is_read === 0) {
        setNotifications((prev) => {
          let currentList = { ...prev };
          currentList.data = currentList?.data?.filter((item) => item?.id !== response?.data?.id);
          return currentList;
        });
      } else {
        setNotifications((prev) => {
          const currentList = { ...prev };
          const updatedNotification = currentList.data.find((item) => item.id === response?.data?.id);
          if (updatedNotification) updatedNotification.is_read = 1;
          return { ...currentList };
        });
      }
      decreaseNotificationCount();
    }
  };

  const handleOpenNotificationDetails = (data, isRequest) => {
    if (isRequest) {
      setOpenCollabRequestDialog(true);
    } else if (data?.meta?.broadcast_message_id) {
      setSelectedBroadcastMessage(data);
      setOpenBroadcastMessageDialog(true);
    } else {
      setOpenCollabRequestUpdateDialog(true);
    }

    if (!data?.is_read) {
      handleReadNotification(data?.id);
    }
    setSelectedCollabRequest(data?.request);
  };

  const handleScroll = (event) => {
    const bottom = event.target.scrollHeight - event.target.scrollTop === event.target.clientHeight;
    if (bottom && !loading) {
      setFilter((prev) => ({
        ...prev,
        per_page: notifications?.per_page + 20
      }));
    }
  };

  const handleFilter = (value) => {
    if (value !== filter?.is_read) {
      setFilter((prev) => ({
        ...prev,
        is_read: value
      }));
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [filter]);

  const notificationCardMap = {
    'user-reported': UserReportedNotificationCard,
    'account-suspended': UserSuspendedNotificationCard,
    'suspension-lifted': UserSuspensionLiftedNotificationCard,
    'new-approved-user': NewApprovedUserNotificationCard,
  };

  return (
    <Box height={"100%"} display={"flex"} flexDirection={"column"}>
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
        <Typography fontSize={isTopBar ? '16px' : '20px'} color="primary" fontWeight={700}>Notifications {isTopBar && `(${notificationsCount ?? 0})`}</Typography>
        <GenericIconLabel
          typographyProps={{ fontWeight: 700 }}
          color={!notificationsCount ? 'grey.light' : 'primary.main'}
          label={'Mark all as read'}
          icon={<Check size={16} weight="bold" />}
          sx={{ cursor: notificationsCount ? 'pointer' : 'default' }}
          onClick={() => handleMarkAllAsRead()}
        />
      </Stack>
      {
        isTopBar ? <GenericDivider /> : <Stack direction={'row'} gap={1} margin={`15px 0 ${loading ? '8px' : '15px'} 0`} justifyContent={'center'}>
          <Button variant={filter?.is_read === 0 ? "outlined" : "contained"} className="longButton" size="large" onClick={() => handleFilter(null)}>All</Button>
          <Button variant={filter?.is_read === null ? "outlined" : "contained"} className="longButton" size="large" onClick={() => handleFilter(0)}>Unread ({notificationsCount ?? 0})</Button>
        </Stack>
      }
      {loading && <GenericLinearLoader margin={'0px 0px 5px 0px'} />}
      {notifications?.data?.length > 0 ?
        (
          <Box
            {...(notifications?.per_page < notifications?.total && { onScroll: handleScroll })}
            sx={{ flexDirection: "column", overflowY: 'auto', gap: .2, display: "flex" }}
            maxHeight={isTopBar ? 448 : "100%"}
          >
            {notifications?.data?.map((notification, index) => {
              const CardComponent = notificationCardMap[notification.type] || DefaultNotificationCard;

              // Check if component is DefaultNotificationCard to pass extra props
              const isDefaultCard = CardComponent === DefaultNotificationCard;

              return (
                <CardComponent
                  key={`notification-card-${notification.id || index}`}
                  notification={notification}
                  isTopBar={isTopBar}
                  isLastItem={index === notifications?.data?.length - 1}
                  handleReadNotification={handleReadNotification}
                  {...(isDefaultCard && {
                    index,
                    notifications: notifications,
                    handleOpenNotificationDetails: handleOpenNotificationDetails,
                    hasPermission: hasPermission,
                  })}
                />
              );
            })}
          </Box>
        ) : (
          <Box sx={{ height: "100%", display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Stack alignItems={'center'} gap={1.5}>
              <Typography variant="subtitle1" fontWeight={700}>You&apos;re all caught up! No new <br></br>notifications at the moment.</Typography>
              <Button variant="contained" className="longButton" onClick={() => router.visit('/collab-creators')}>Explore Creators</Button>
            </Stack>
          </Box>
        )
      }
      {
        isTopBar && <Fragment>
          <GenericDivider />
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button variant="outlined" className="longButton" onClick={() => router.visit('/overview')}>See All Notifications</Button>
          </Box>
        </Fragment>
      }
      <CollabRequestDialog
        refetchData={() => fetchNotifications()}
        collabRequestDetails={selectedCollabRequest}
        openCollabRequestDialog={openCollabRequestDialog}
        handleCloseRequestDialog={() => setOpenCollabRequestDialog(false)}
      />
      <CollabRequestUpdateDialog
        openCollabRequestUpdateDialog={openCollabRequestUpdateDialog}
        handleCloseRequestUpdateDialog={() => handleCloseCollabRequestUpdateDialog()}
        collabRequestDetails={selectedCollabRequest}
      />
      <BroadcastMessageDialog
        openCollabRequestUpdateDialog={openBroadcastMessageDialog}
        handleCloseRequestUpdateDialog={() => setOpenBroadcastMessageDialog(false)}
        broadcastMessageDetails={selectedBroadcastMessage}
      />
    </Box >
  );
};

// this old code used to render the notifications
const DefaultNotificationCard = ({ notification, isTopBar, index, notifications, handleOpenNotificationDetails, hasPermission }) => {
  const { CAN_VIEW_CHATS_PAGE } = PAGE_PERMISSIONS;

  const isCancelled = (notification?.request?.status === CANCELLED);
  const requested_details = (notification?.title === NEW_COLLAB_REQUEST_TITLE || isCancelled) ? notification?.request?.requested_by : notification?.request?.requested_to;
  const isBroadcast = BROADCAST_MESSAGE_TYPES.includes(notification.title);

  const avatarLocation = isBroadcast
    ? notification?.broadcast_creator_profile_picture
    : requested_details?.details?.profile_picture?.location;

  return (
    <Box key={`notification-key-${index}`}>
      <Box sx={{ height: isTopBar ? '112px' : '135px', backgroundColor: !notification?.is_read && 'background.lightBlue', borderRadius: '10px', p: 2 }}>
        <Grid container>
          <Grid item gap={2} size={isTopBar ? 7 : 8}>
            <Stack direction={'row'} gap={isTopBar ? 1 : 2} alignItems={'center'}>
              <GenericAvatar size={isTopBar ? '32px' : '46px'} location={avatarLocation} />
              <Box>
                <Typography sx={{ fontSize: isTopBar ? '14px' : '16px', width: { xs: 140, sm: isTopBar ? 130 : '100%' } }} fontWeight={700} noWrap>{notification?.title}</Typography>
                <Typography
                  sx={{ fontSize: isTopBar ? '12px' : '14px' }}
                  width={{ xs: isTopBar ? 200 : 210, sm: isTopBar ? 199 : 325, lg: isTopBar ? 199 : 400, xl: isTopBar ? 199 : 400 }}
                  noWrap
                  color="text.light"
                >
                  {notification?.request ? (
                    <Fragment>
                      <b>{notification?.request?.status === CANCELLED ? notification?.request?.requested_by?.name : requested_details?.name}</b> {NOTIFICATIONS_MESSAGE[notification?.title]}
                    </Fragment>
                  ) : (
                    notification?.meta?.broadcast_title
                  )}
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid container item justifyContent={'flex-end'} size={isTopBar ? 5 : 4}>
            <Typography variant="body2" color="text.light">{formatDistanceToNow(new Date(notification?.created_at))}</Typography>
          </Grid>
        </Grid>
        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          {
            isTopBar == false && notification?.title !== 'Collab Accepted' && notification?.request?.status == ACCEPTED && (hasPermission(CAN_VIEW_CHATS_PAGE)) &&
            <Button
              variant="contained"
              sx={{ width: isTopBar ? '240px' : '250px' }}
              size={isTopBar ? 'small' : "large"}
              onClick={() => hasPermission(CAN_VIEW_CHATS_PAGE) && window.open(`/chats`, '_blank')}
            >
              Open Chat
            </Button>
          }
          <Button
            variant="contained"
            sx={{ width: isTopBar ? '240px' : '250px' }}
            size={isTopBar ? 'small' : "large"}
            onClick={() => handleOpenNotificationDetails(notification, notification?.title === NEW_COLLAB_REQUEST_TITLE)}
          >
            {
              !notification?.meta?.broadcast_message_id
                ? (notification?.title === 'Collab Accepted' && hasPermission(CAN_VIEW_CHATS_PAGE)
                  ? 'Start Chatting'
                  : 'View Request')
                : 'View Details'
            }
          </Button>
        </Box>
      </Box>
      {index !== (notifications?.data?.length - 1) && <GenericDivider />}
    </Box>
  );
};

const UserReportedNotificationCard = ({ notification, isTopBar, isLastItem, handleReadNotification }) => {
  const [{ loggedIndUserDetails }] = useStore();
  const handleViewReport = () => {
    if (!notification.is_read) {
      handleReadNotification(notification.id);
    }
  };

  return (
    <Box height={142}>
      <Box
        width={"100%"}
        sx={{
          display: "flex",
          p: 2,
          backgroundColor: !notification?.is_read && "background.lightBlue",
          flexDirection: "row",
          gap: 1.5,
          borderRadius: 3,
        }}
      >
        <GenericAvatar size='32px' location={loggedIndUserDetails?.profile_picture_location} />
        <Box width={"100%"} >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '14px' }} fontWeight={700} noWrap>{notification?.title}</Typography>
            <Typography noWrap variant="body2" color="text.light">{formatDistanceToNow(new Date(notification?.created_at))}</Typography>
          </Box>
          <Typography width={{ xxs: 180, xsm: isTopBar ? 240 : "100%" }} sx={{ overflow: 'hidden', whiteSpace: 'noWrap', textOverflow: 'ellipsis', color: 'text.light' }}>
            {notification.meta.details}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Link href={"/admin-collab-creators"} onClick={handleViewReport} style={{ textDecoration: 'none', width: isTopBar ? '100%' : '250px' }}>
              <Button variant="contained" size={isTopBar ? "small" : "large"} sx={{ width: isTopBar ? "100%" : '250px' }}>View report</Button>
            </Link>
          </Box>
        </Box>
      </Box>
      {!isLastItem && <GenericDivider />}
    </Box>
  );
};

const UserSuspendedNotificationCard = ({ notification, isTopBar, isLastItem, handleReadNotification }) => {
  const { setIsOpen } = useNotificationActions();
  const [{ loggedIndUserDetails }] = useStore();

  const handleViewDetails = () => {
    setIsOpen(true, notification);
    if (!notification.is_read) {
      // TODO: Optimize — this currently triggers a full app re-render
      handleReadNotification(notification.id);
    }
  };

  return (
    <Box height={142}>
      <Box
        width={"100%"}
        sx={{
          display: "flex",
          p: 2,
          backgroundColor: !notification?.is_read && "background.lightBlue",
          flexDirection: "row",
          gap: 1.5,
          borderRadius: 3,
        }}
      >
        <GenericAvatar size='32px' location={loggedIndUserDetails?.profile_picture_location} />
        <Box width={"100%"} >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '14px' }} fontWeight={700} noWrap>{notification?.title}</Typography>
            <Typography noWrap variant="body2" color="text.light">{formatDistanceToNow(new Date(notification?.created_at))}</Typography>
          </Box>
          <Typography width={{ xxs: 180, xsm: isTopBar ? 240 : "100%" }} sx={{ overflow: 'hidden', whiteSpace: 'noWrap', textOverflow: 'ellipsis', color: 'text.light' }}>
            {notification.meta.details}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button onClick={handleViewDetails} variant="contained" size={isTopBar ? "small" : "large"} sx={{ width: isTopBar ? "100%" : '250px' }}>View details</Button>
          </Box>
        </Box>
      </Box>
      {!isLastItem && <GenericDivider />}
    </Box>
  );
};

const UserSuspensionLiftedNotificationCard = ({ notification, isTopBar, isLastItem, handleReadNotification }) => {
  const { setIsOpen } = useNotificationActions();
  const [{ loggedIndUserDetails }] = useStore();

  const handleViewDetails = () => {
    setIsOpen(true, notification);

    if (!notification.is_read) {
      // TODO: Optimize — this currently triggers a full app re-render
      handleReadNotification(notification.id);
    }
  };

  return (
    <Box height={142}>
      <Box
        width={"100%"}
        sx={{
          display: "flex",
          p: 2,
          backgroundColor: !notification?.is_read && "background.lightBlue",
          flexDirection: "row",
          gap: 1.5,
          borderRadius: 3,
        }}
      >
        <GenericAvatar size='32px' location={loggedIndUserDetails?.profile_picture_location} />
        <Box width={"100%"} >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '14px' }} fontWeight={700} noWrap>{notification?.title}</Typography>
            <Typography noWrap variant="body2" color="text.light">{formatDistanceToNow(new Date(notification?.created_at))}</Typography>
          </Box>
          <Typography width={{ xxs: 180, xsm: isTopBar ? 240 : "100%" }} sx={{ overflow: 'hidden', whiteSpace: 'noWrap', textOverflow: 'ellipsis', color: 'text.light' }}>
            {notification.meta.details}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button onClick={handleViewDetails} variant="contained" size={isTopBar ? "small" : "large"} sx={{ width: isTopBar ? "100%" : '250px' }}>View details</Button>
          </Box>
        </Box>
      </Box>
      {!isLastItem && <GenericDivider />}
    </Box>
  );
};

const NewApprovedUserNotificationCard = ({ notification, isTopBar, isLastItem, handleReadNotification }) => {
  const [{ loggedIndUserDetails }] = useStore();

  const handleViewProfile = () => {
    router.visit(
      `/creator?user=${toSlug(notification?.meta?.user?.name)}-${notification?.meta?.user?.id}`
    );

    if (!notification.is_read) {
      // TODO: Optimize — this currently triggers a full app re-render
      handleReadNotification(notification.id);
    }
  };

  return (
    <Box height={142}>
      <Box
        width={"100%"}
        sx={{
          display: "flex",
          p: 2,
          backgroundColor: !notification?.is_read && "background.lightBlue",
          flexDirection: "row",
          gap: 1.5,
          borderRadius: 3,
        }}
      >
        <GenericAvatar size='32px' location={notification.meta.notification_avatar ?? loggedIndUserDetails?.profile_picture_location} />
        <Box width={"100%"} >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '14px' }} fontWeight={700} noWrap>{notification?.title}</Typography>
            <Typography noWrap variant="body2" color="text.light">{formatDistanceToNow(new Date(notification?.created_at))}</Typography>
          </Box>
          <Typography width={{ xxs: 180, xsm: isTopBar ? 240 : "100%" }} sx={{ overflow: 'hidden', whiteSpace: 'noWrap', textOverflow: 'ellipsis', color: 'text.light' }}>
            {notification.meta.details}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button onClick={handleViewProfile} variant="contained" size={isTopBar ? "small" : "large"} sx={{ width: isTopBar ? "100%" : '250px' }}>View Profile</Button>
          </Box>
        </Box>
      </Box>
      {!isLastItem && <GenericDivider />}
    </Box>
  );
};
